import {UtensilsCrossed, Beef, Flame} from 'lucide-react'
import type { Meal } from '@/lib/plans'

interface Props {
  meals: Meal[]
  dailyCalories: number
  totalProtein: number
}

function MealPlanner({ meals, dailyCalories, totalProtein }: Props) {
  return (
    <section id="meals" className="relative py-20 lg:py-28 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-16 items-start">
          {/* Intro / SEO copy */}
          <header className="lg:sticky lg:top-28">
            <span className="font-mono text-[13px] uppercase tracking-[0.2em] text-blue-500">
              Nutrition
            </span>
            <h2 className="font-display mt-3 text-[34px] sm:text-[42px] font-bold tracking-[-0.02em] text-white">
              The best meals to get ripped
            </h2>
            <p className="mt-4 text-[16px] leading-relaxed text-slate-400">
              This interactive meal planner auto-generates low-calorie, high-protein blueprints
              built around your exact daily target. The right food to get ripped keeps you full,
              spares muscle in a deficit, and makes shredding in bodybuilding sustainable.
            </p>

            <div className="mt-7 grid grid-cols-2 gap-4 max-w-sm">
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-5">
                <Flame className="w-5 h-5 text-blue-400" aria-hidden="true" />
                <div className="font-mono text-2xl font-bold text-white mt-3">
                  {dailyCalories.toLocaleString()}
                </div>
                <div className="text-[12px] text-slate-500 mt-0.5">kcal across 4 meals</div>
              </div>
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-5">
                <Beef className="w-5 h-5 text-cyan-400" aria-hidden="true" />
                <div className="font-mono text-2xl font-bold text-white mt-3">
                  {totalProtein}g
                </div>
                <div className="text-[12px] text-slate-500 mt-0.5">muscle-sparing protein</div>
              </div>
            </div>
          </header>

          {/* Meal cards */}
          <div className="space-y-4">
            {meals.map((m, i) => (
              <article
                key={m.name}
                className="group rounded-3xl border border-white/[0.07] bg-white/[0.02] p-6 hover:border-blue-500/30 hover:bg-white/[0.035] transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <span className="grid place-items-center w-11 h-11 rounded-2xl bg-blue-600/15 border border-blue-500/20 text-blue-400 font-mono font-bold">
                      {i + 1}
                    </span>
                    <div>
                      <h3 className="font-display text-[18px] font-semibold text-white">
                        {m.name}
                      </h3>
                      <span className="text-[12px] text-blue-400/80">{m.tag}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-[18px] font-bold text-white">
                      {m.calories}
                      <span className="text-[12px] text-slate-500"> kcal</span>
                    </div>
                    <div className="font-mono text-[13px] text-cyan-400">{m.protein}g protein</div>
                  </div>
                </div>

                <ul className="mt-4 flex flex-wrap gap-2">
                  {m.items.map((item) => (
                    <li
                      key={item}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.07] bg-white/[0.02] px-3.5 py-1.5 text-[13px] text-slate-300"
                    >
                      <UtensilsCrossed className="w-3 h-3 text-slate-600" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default MealPlanner
